import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import DOMPurify from 'dompurify';

const ViewForm = () => {
  const [formData, setFormData] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [headerImage, setHeaderImage] = useState(null);
  const [headerImagePosition, setHeaderImagePosition] = useState('center');
  const [headerImageSize, setHeaderImageSize] = useState('medium');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState({});
  const [uploadedFiles, setUploadedFiles] = useState({});
  const { id } = useParams();
  const [images, setImages] = useState({});
  const [formNameColor, setFormNameColor] = useState('#000000');
  const [otherOptions, setOtherOptions] = useState({});
  const [tickets, setTickets] = useState([]);

  const styles = document.createElement('style');
  styles.textContent = `
  .block.font-medium a {
    color: #2563eb !important;
    text-decoration: underline !important;
    cursor: pointer !important;
  }
  .block.font-medium a:hover {
    color: #1d4ed8 !important;
  }
`;
  document.head.appendChild(styles);

  // Primero, añade esta función de configuración de DOMPurify al inicio del componente ViewForm
  const config = {
    ADD_ATTR: ['target', 'rel', 'style', 'class'],
    ADD_TAGS: ['a'],
    ALLOWED_TAGS: ['p', 'a', 'strong', 'em', 'span', 'br', 'ul', 'ol', 'li'],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'style'],
    WHOLE_DOCUMENT: false,
    transformTags: {
      a: (tagName, attribs) => ({
        tagName,
        attribs: {
          ...attribs,
          style: 'color: #2563eb; text-decoration: underline; cursor: pointer;',
          target: '_blank',
          rel: 'noopener noreferrer',
        },
      }),
    },
  };

  const stripHtml = html => {
    if (!html) return '';
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || '';
  };

  useEffect(() => {
    if (!id) {
      setError('ID de formulario no proporcionado.');
      return;
    }
    fetchFormData(id);
  }, [id]);

  useEffect(() => {
    if (!formData) return;
    const hasTicketSelector = formData.fields.some(
      f => f.type === 'ticketSelector'
    );
    if (!hasTicketSelector) return;
    fetch(
      `${process.env.REACT_APP_URL_BASE_BACKEND}/api/forms/${id}/pending-tickets`,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      }
    )
      .then(res => res.json())
      .then(data => {
        console.log('Respuesta de tickets pendientes:', data);
        if (Array.isArray(data)) {
          setTickets(data);
        } else {
          setTickets([]);
          console.error('La respuesta de tickets no es un array:', data);
        }
      })
      .catch(err => {
        console.error('Error al obtener tickets pendientes:', err);
        setTickets([]);
      });
  }, [formData, id]);

  // Añade estos estilos después de la declaración de estados
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
    .space-y-2 span a {
      color: #2563eb !important;
      text-decoration: underline !important;
      cursor: pointer !important;
    }
    .space-y-2 span a:hover {
      color: #1d4ed8 !important;
    }
    .space-y-2 span ul {
      list-style-type: disc;
      padding-left: 1.5em;
    }
    .space-y-2 span li {
      margin-bottom: 0.5em;
    }
  `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  const fetchFormData = async id => {
    setIsLoading(true);
    try {
      const response = await fetch(
        process.env.REACT_APP_URL_BASE_BACKEND + `/api/forms/${id}`
      );
      if (!response.ok) {
        throw new Error('Error al obtener los datos del formulario');
      }
      const data = await response.json();
      console.log(
        'Estructura completa del formulario:',
        JSON.stringify(data, null, 2)
      );

      console.log('Datos del formulario recibidos:', data);
      console.log(
        '🖥️ Renderizando formulario con botón:',
        JSON.stringify(formData?.submitButton, null, 2)
      );
      console.log(
        '📩 Datos recibidos desde el backend:',
        JSON.stringify(formData, null, 2)
      );
      console.log('Datos de la imagen:', data.headerImage); // Debug log

      setFormData(data);
      setFormNameColor(data.formNameColor || '#000000');
      // Check for headerImage in different possible locations
      const headerImageData = data.headerImage || data.formImage || data.image;
      console.log('Datos de imagen encontrados:', headerImageData);

      if (headerImageData) {
        if (typeof headerImageData === 'string') {
          // If headerImage is just a URL string
          setHeaderImage(headerImageData);
          setHeaderImagePosition('center');
          setHeaderImageSize('medium');
        } else {
          // If headerImage is an object with properties
          setHeaderImage(headerImageData.url);
          setHeaderImagePosition(headerImageData.position || 'center');
          setHeaderImageSize(headerImageData.size || 'medium');
        }
      }
    } catch (err) {
      console.error(err);
      setError('No se pudo cargar el formulario.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileUpload = async (event, fieldId, fileType = 'image') => {
    const file = event.target.files[0];
    if (!file) return;

    const submitButton = document.getElementById('submitButton');
    if (submitButton) submitButton.disabled = true;

    const loadingIndicator = document.getElementById('loadingIndicator');
    if (loadingIndicator) loadingIndicator.style.display = 'block';

    // Actualizar el estado global de carga
    setIsUploading(true);
    setUploadingFiles(prev => ({ ...prev, [fieldId]: true }));

    // Crear una URL de vista previa para la imagen
    const previewUrl = fileType === 'image' ? URL.createObjectURL(file) : null;
    setUploadedFiles(prev => ({
      ...prev,
      [fieldId]: {
        previewUrl,
        status: 'uploading',
        file: file,
        fileType,
      },
    }));

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append(
        'fileType',
        file.type.startsWith('image/') ? 'image' : 'document'
      );

      // Mostrar un spinner o indicador de carga mientras se sube el archivo
      const response = await fetch(
        `${process.env.REACT_APP_URL_BASE_BACKEND}/api/upload-file`,
        {
          method: 'POST',
          body: formData,
        }
      );

      if (!response.ok) throw new Error('Error al subir el archivo');

      const data = await response.json();
      setUploadedFiles(prev => ({
        ...prev,
        [fieldId]: {
          uploadedUrl: data.fileUrl,
          previewUrl,
          status: 'completed',
          file: file,
          fileType,
          fileName: file.name,
        },
      }));
    } catch (error) {
      console.error('Error al subir archivo:', error);
      setUploadedFiles(prev => ({
        ...prev,
        [fieldId]: {
          status: 'error',
          fileType,
        },
      }));
      Swal.fire({
        title: 'Error',
        text: 'No se pudo subir el archivo',
        icon: 'error',
      });
    } finally {
      // Habilitar el botón de envío nuevamente
      if (submitButton) submitButton.disabled = false;

      // Ocultar el indicador de carga
      if (loadingIndicator) loadingIndicator.style.display = 'none';

      setUploadingFiles(prev => ({ ...prev, [fieldId]: false }));

      // Verificar si aún hay archivos subiendo
      const stillUploading = Object.values(uploadingFiles).some(
        value => value === true
      );
      if (!stillUploading) {
        setIsUploading(false);
      }
    }
  };

  const isSubmitDisabled =
    isUploading || Object.values(uploadingFiles).some(value => value === true);

  const titleConfig = {
    ADD_ATTR: ['target', 'rel', 'style', 'class'],
    ADD_TAGS: ['a', 'p'],
    ALLOWED_TAGS: ['p', 'a', 'strong', 'em', 'span', 'br', 'ul', 'ol', 'li'],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'style'],
    transformTags: {
      a: (tagName, attribs) => ({
        tagName,
        attribs: {
          ...attribs,
          target: '_blank',
          rel: 'noopener noreferrer',
          style: 'color: #2563eb; text-decoration: underline; cursor: pointer;',
        },
      }),
    },
  };

  // Add this helper function at the top of your component
  const getDateRestrictions = field => {
    const today = new Date().toISOString().split('T')[0];

    switch (field.dateRestriction) {
      case 'past':
        return { max: today };
      case 'future':
        return { min: today };
      case 'range':
        return {
          min: field.minDate
            ? new Date(field.minDate).toISOString().split('T')[0]
            : undefined,
          max: field.maxDate
            ? new Date(field.maxDate).toISOString().split('T')[0]
            : undefined,
        };
      default:
        return {};
    }
  };

  const handleSubmit = async event => {
    event.preventDefault();
    const formValues = new FormData(event.target);
    const values = {};
    const uploadedImages = [];
    const uploadedDocuments = [];

    const fileUrl = document.getElementById('fileUrlInput')?.value;
    if (fileUrl) {
      // Puedes agregar la URL del archivo a los valores o hacer lo que necesites con ella
      values['fileUrl'] = fileUrl;
    }
    for (const field of formData.fields) {
      if (field.type === 'file') {
        const fileData = uploadedFiles[field.id];
        if (fileData?.uploadedUrl) {
          if (fileData.fileType === 'image') {
            uploadedImages.push({
              fieldId: field.id,
              fieldLabel: field.label,
              imageUrl: fileData.uploadedUrl,
              originalName: fileData.fileName,
              mimeType: fileData.file?.type,
            });
          } else {
            // Para documentos PDF
            uploadedDocuments.push({
              fieldId: field.id,
              fieldLabel: field.label,
              documentUrl: fileData.uploadedUrl,
              originalName: fileData.fileName,
              mimeType: fileData.file?.type,
            });
          }
        } else if (field.required) {
          Swal.fire({
            title: 'Error',
            text: `Por favor sube una imagen para ${field.label}`,
            icon: 'error',
          });
          return;
        }
      } else if (field.type === 'checkbox') {
        // Manejar múltiples selecciones de checkbox
        const checkboxValues = formValues.getAll(field.id);
        if (checkboxValues.length > 0) {
          values[field.label] = checkboxValues;
        }
      } else if (field.type === 'date' && field.dateRestriction === 'range') {
        // Handle date range fields
        const startValue = formValues.get(`${field.id}-start`);
        const endValue = formValues.get(`${field.id}-end`);

        if (startValue && endValue) {
          values[field.label] = `${startValue} - ${endValue}`;
        } else if (startValue) {
          values[field.label] = startValue;
        } else if (endValue) {
          values[field.label] = endValue;
        }
      } else if (field.type === 'ticketSelector') {
          const ticketNumber = formValues.get(field.id);
          if (ticketNumber) {
            values[field.label] = ticketNumber;
          }
      } else {
        const value = formValues.get(field.id);
        if (value) {
          values[field.label] = value;
        }
      }

      // Código añadido para procesar las opciones "Otro"
      if (
        ['select', 'radio', 'checkbox'].includes(field.type) &&
        field.includeOtherOption
      ) {
        const fieldValue = formValues.get(field.id);
        if (fieldValue === 'otro') {
          const otherValue = formValues.get(`${field.id}-other`);
          if (otherValue) {
            values[field.label] = otherValue;
          }
        } else if (field.type === 'checkbox') {
          // Para checkbox, verificar si la opción "Otro" está marcada
          const otherChecked =
            formValues.get(`${field.id}-other-checkbox`) === 'otro';
          if (otherChecked) {
            const otherValue = formValues.get(`${field.id}-other`);
            if (otherValue) {
              // Añadir el valor "Otro" a la lista de valores seleccionados
              if (values[field.label]) {
                if (Array.isArray(values[field.label])) {
                  values[field.label].push(otherValue);
                } else {
                  values[field.label] = [values[field.label], otherValue];
                }
              } else {
                values[field.label] = [otherValue];
              }
            }
          }
        }
      }
    }

    const responseConfig = formData.responseConfig || {};
    responseConfig.buttonSettings = {
      text: formData.buttonSettings?.text || 'Enviar',
      backgroundColor: formData.buttonSettings?.backgroundColor || '#2563eb',
      textColor: formData.buttonSettings?.textColor || '#ffffff',
      borderRadius: formData.buttonSettings?.borderRadius || '0.375rem',
    };

    console.log('Datos a enviar:', {
      responses: values,
      images: uploadedImages,
      documents: uploadedDocuments,
      // Si estás utilizando fileUrl, asegúrate de incluirlo aquí
      fileUrl: document.getElementById('fileUrlInput')?.value,
    });

    try {
      if (!id) {
        throw new Error('ID del formulario no válido');
      }

      console.log('Enviando respuesta:', {
        responses: values,
        images: uploadedImages,
      });

      const response = await fetch(
        process.env.REACT_APP_URL_BASE_BACKEND + `/forms/${id}/responses`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            responses: values,
            images: uploadedImages,
            documents: uploadedDocuments,
            responseConfig,
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Error al enviar las respuestas');
      }

      const responseText = await response.text();

      if (!response.ok) {
        console.error('Error del servidor:', responseText);
        throw new Error(
          `Error al enviar las respuestas: ${response.status} ${response.statusText}`
        );
      }

      const responseData = response.ok ? JSON.parse(responseText) : null;
      console.log('Respuesta recibida:', responseData);

      // Limpiar los campos del formulario
      event.target.reset();
      setImages({});

      if (
        responseData.responseConfig.responseType === 'redirect' &&
        responseData.responseConfig.redirectUrl
      ) {
        window.location.href = responseData.responseConfig.redirectUrl;
      } else {
        Swal.fire({
          title:
            responseData.responseConfig.alertSettings?.title ||
            '¡Gracias por tu respuesta!',
          text:
            responseData.responseConfig.alertSettings?.message ||
            'Tus respuestas han sido registradas exitosamente.',
          icon: responseData.responseConfig.alertSettings?.type || 'success',
          confirmButtonText: 'Aceptar',
          background:
            responseData.responseConfig.alertSettings?.backgroundColor ||
            '#ffffff',
          color:
            responseData.responseConfig.alertSettings?.textColor || '#000000',
        });
      }
    } catch (error) {
      console.error('Error detallado:', error);
      Swal.fire({
        title: 'Error',
        text: `Hubo un error al enviar las respuestas: ${error.message}`,
        icon: 'error',
      });
    }
  };

  if (isLoading) return <p>Cargando formulario...</p>;
  if (error) return <p className="text-error">{error}</p>;

  // Añade los patrones de validación después de las declaraciones de estado
  const validationPatterns = {
    letters: /^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]*$/,
    numbers: /^\d*$/,
    alphanumeric: /^[a-zA-Z0-9áéíóúÁÉÍÓÚñÑ\s]*$/,
    email: /^[a-zA-Z0-9._-]*@?[a-zA-Z0-9.-]*\.?[a-zA-Z]{0,}$/,
    phone: /^[0-9-+\s()]*$/,
  };

  // Añade la función handleInputValidation antes del return
  const handleInputValidation = (e, validationType) => {
    if (!validationType || validationType === 'none') return;

    const value = e.target.value;
    const field = formData.fields.find(f => f.id === e.target.name);

    // Check maxLength first
    if (field?.maxLength && value.length > field.maxLength) {
      e.target.value = value.slice(0, field.maxLength);
      return;
    }

    if (validationType === 'email') {
      // Final validation when form is submitted
      if (value.includes('@')) {
        const emailPattern = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        if (!emailPattern.test(value)) {
          e.preventDefault();
          e.target.value = value.slice(0, -1);

          // Show validation message
          if (!e.target.nextElementSibling?.classList.contains('email-error')) {
            const errorMsg = document.createElement('span');
            errorMsg.className = 'email-error text-red-500 text-sm mt-1';
            errorMsg.textContent =
              'Email inválido. Debe incluir @ y dominio válido (ej: .com, .co, .org)';
            e.target.parentNode.insertBefore(errorMsg, e.target.nextSibling);
          }
        } else {
          // Remove error message if email is valid
          const errorMsg = e.target.nextElementSibling;
          if (errorMsg?.classList.contains('email-error')) {
            errorMsg.remove();
          }
        }
      }
    } else if (
      validationPatterns[validationType] &&
      !validationPatterns[validationType].test(value)
    ) {
      e.preventDefault();
      e.target.value = value.slice(0, -1);
    }
  };

  const handleKeyDown = (e, validationType) => {
    if (
      validationType === 'numbers' &&
      !/^\d$/.test(e.key) &&
      !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(e.key)
    ) {
      e.preventDefault();
    }

    if (validationType === 'email' && e.key === ' ') {
      e.preventDefault();
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-4">
      {formData ? (
        <form onSubmit={handleSubmit} className="space-y-6">
          {headerImage && (
            <div
              className="w-full mb-6"
              style={{
                textAlign: headerImagePosition,
              }}
            >
              {console.log('Renderizando imagen:', {
                url: headerImage,
                position: headerImagePosition,
                size: headerImageSize,
              })}
              <img
                src={headerImage}
                alt="Encabezado"
                style={{
                  display: 'inline-block',
                  maxWidth:
                    headerImageSize === 'small'
                      ? '200px'
                      : headerImageSize === 'medium'
                      ? '400px'
                      : '600px',
                  height: 'auto',
                }}
                onError={e => {
                  console.error('Error cargando imagen:', e);
                  console.log('URL de la imagen:', headerImage);
                }}
              />
            </div>
          )}
          <div
            className="text-2xl font-bold text-center mb-6"
            style={{ color: formNameColor }}
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(formData.formName, titleConfig),
            }}
          />
          {formData.fields.map(field => (
            <div key={field.id} className="space-y-2">
              {/* Aca es la vista de las preguntas */}
              <label
                className="block font-medium"
                style={{ color: field.labelColor }}
                dangerouslySetInnerHTML={{
                  __html:
                    DOMPurify.sanitize(field.label, config) +
                    (field.required
                      ? '<span class="text-red-500 ml-1">*</span>'
                      : ''),
                }}
              />
              {field.type === 'file' ? (
                <div className="space-y-2">
                  <input
                    type="file"
                    accept="image/*,application/pdf"
                    onChange={e => handleFileUpload(e, field.id)}
                    className="border p-2 w-full"
                    style={{
                      backgroundColor: field.backgroundColor,
                      borderColor: field.borderColor,
                      borderRadius:
                        field.borderRadius === 'rounded'
                          ? '0.375rem'
                          : field.borderRadius === 'rounded-md'
                          ? '0.5rem'
                          : field.borderRadius === 'rounded-lg'
                          ? '0.75rem'
                          : '0',
                    }}
                  />
                  {uploadingFiles[field.id] && (
                    <div className="flex items-center mt-2">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500 mr-2"></div>
                      <span>Subiendo archivo...</span>
                    </div>
                  )}
                  {uploadedFiles[field.id]?.status === 'completed' && (
                    <div className="mt-2">
                      <div className="flex items-center text-green-600">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5 mr-1"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                            clipRule="evenodd"
                          />
                        </svg>
                        <span>Archivo subido exitosamente</span>
                      </div>
                      {uploadedFiles[field.id].previewUrl &&
                        uploadedFiles[field.id].file?.type.startsWith(
                          'image/'
                        ) && (
                          <div className="mt-2">
                            <img
                              src={uploadedFiles[field.id].previewUrl}
                              alt="Vista previa"
                              className="max-h-40 rounded"
                            />
                          </div>
                        )}
                      {uploadedFiles[field.id].file?.type ===
                        'application/pdf' && (
                        <div className="mt-2 flex items-center">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-8 w-8 text-red-500 mr-2"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z"
                              clipRule="evenodd"
                            />
                          </svg>
                          <a
                            href={uploadedFiles[field.id].uploadedUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline"
                          >
                            {uploadedFiles[field.id].file.name}
                          </a>
                        </div>
                      )}
                    </div>
                  )}
                  {uploadedFiles[field.id]?.status === 'error' && (
                    <div className="mt-2 text-red-500 flex items-center">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5 mr-1"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span>
                        Error al subir archivo. Por favor, intenta de nuevo.
                      </span>
                    </div>
                  )}
                </div>
              ) : field.type === 'textarea' ? (
                <textarea
                  className="w-full min-h-[100px] border"
                  name={field.id}
                  placeholder={field.placeholder || ''}
                  required={field.required}
                  style={{
                    backgroundColor: field.backgroundColor,
                    color: field.labelColor,
                    borderColor: field.borderColor,
                    borderRadius:
                      field.borderRadius === 'rounded'
                        ? '0.375rem'
                        : field.borderRadius === 'rounded-md'
                        ? '0.5rem'
                        : field.borderRadius === 'rounded-lg'
                        ? '0.75rem'
                        : '0',
                    padding:
                      field.padding === 'p-2'
                        ? '0.5rem'
                        : field.padding === 'p-4'
                        ? '1rem'
                        : field.padding === 'p-6'
                        ? '1.5rem'
                        : '1rem',
                  }}
                />
              ) : field.type === 'date' && field.dateRestriction === 'range' ? (
                <div className="flex gap-2">
                  <input
                    className="w-1/2 border"
                    type="date"
                    name={`${field.id}-start`}
                    placeholder="Fecha mínima"
                    required={field.required}
                    min={field.minDate || ''}
                    max={field.maxDate || ''}
                    style={{
                      backgroundColor: field.backgroundColor,
                      color: field.labelColor,
                      borderColor: field.borderColor,
                      borderRadius:
                        field.borderRadius === 'rounded'
                          ? '0.375rem'
                          : field.borderRadius === 'rounded-md'
                          ? '0.5rem'
                          : field.borderRadius === 'rounded-lg'
                          ? '0.75rem'
                          : '0',
                      padding:
                        field.padding === 'p-2'
                          ? '0.5rem'
                          : field.padding === 'p-4'
                          ? '1rem'
                          : field.padding === 'p-6'
                          ? '1.5rem'
                          : '1rem',
                    }}
                  />
                  <input
                    className="w-1/2 border"
                    type="date"
                    name={`${field.id}-end`}
                    placeholder="Fecha máxima"
                    required={field.required}
                    min={field.minDate || ''}
                    max={field.maxDate || ''}
                    style={{
                      backgroundColor: field.backgroundColor,
                      color: field.labelColor,
                      borderColor: field.borderColor,
                      borderRadius:
                        field.borderRadius === 'rounded'
                          ? '0.375rem'
                          : field.borderRadius === 'rounded-md'
                          ? '0.5rem'
                          : field.borderRadius === 'rounded-lg'
                          ? '0.75rem'
                          : '0',
                      padding:
                        field.padding === 'p-2'
                          ? '0.5rem'
                          : field.padding === 'p-4'
                          ? '1rem'
                          : field.padding === 'p-6'
                          ? '1.5rem'
                          : '1rem',
                    }}
                  />
                </div>
              ) : field.type === 'select' ? (
                <div>
                  <select
                    className="w-full border"
                    name={field.id}
                    required={field.required}
                    onChange={e => {
                      if (e.target.value === 'otro') {
                        setOtherOptions({ ...otherOptions, [field.id]: true });
                      } else {
                        setOtherOptions({ ...otherOptions, [field.id]: false });
                      }
                    }}
                    style={{
                      backgroundColor: field.backgroundColor,
                      color: field.labelColor,
                      borderColor: field.borderColor,
                      borderRadius:
                        field.borderRadius === 'rounded'
                          ? '0.375rem'
                          : field.borderRadius === 'rounded-md'
                          ? '0.5rem'
                          : field.borderRadius === 'rounded-lg'
                          ? '0.75rem'
                          : '0',
                      padding:
                        field.padding === 'p-2'
                          ? '0.5rem'
                          : field.padding === 'p-4'
                          ? '1rem'
                          : field.padding === 'p-6'
                          ? '1.5rem'
                          : '1rem',
                    }}
                  >
                    <option value="">Seleccionar</option>
                    {field.options?.map((option, index) => {
                      const cleanOption = stripHtml(option);
                      return (
                        <option key={index} value={cleanOption}>
                          {cleanOption}
                        </option>
                      );
                    })}
                    {field.includeOtherOption && (
                      <option value="otro">Otro</option>
                    )}
                  </select>
                  {otherOptions[field.id] && (
                    <input
                      type="text"
                      className="w-full border mt-2"
                      name={`${field.id}-other`}
                      placeholder={
                        field.otherOptionPlaceholder ||
                        'Especifique otra opción...'
                      }
                      required
                      style={{
                        backgroundColor: field.backgroundColor,
                        color: field.labelColor,
                        borderColor: field.borderColor,
                        borderRadius:
                          field.borderRadius === 'rounded'
                            ? '0.375rem'
                            : field.borderRadius === 'rounded-md'
                            ? '0.5rem'
                            : field.borderRadius === 'rounded-lg'
                            ? '0.75rem'
                            : '0',
                        padding:
                          field.padding === 'p-2'
                            ? '0.5rem'
                            : field.padding === 'p-4'
                            ? '1rem'
                            : field.padding === 'p-6'
                            ? '1.5rem'
                            : '1rem',
                      }}
                    />
                  )}
                </div>
              ) : field.type === 'checkbox' ? (
                <div className="space-y-2">
                  {field.options?.map((option, index) => (
                    <label key={index} className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        name={field.id}
                        value={option}
                        style={{
                          accentColor: field.labelColor,
                          borderColor: field.borderColor,
                        }}
                      />
                      <span
                        style={{ color: field.labelColor }}
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(option, config),
                        }}
                      />
                    </label>
                  ))}
                  {field.includeOtherOption && (
                    <>
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          name={`${field.id}-other-checkbox`}
                          value="otro"
                          onChange={e =>
                            setOtherOptions({
                              ...otherOptions,
                              [field.id]: e.target.checked,
                            })
                          }
                          style={{
                            accentColor: field.labelColor,
                            borderColor: field.borderColor,
                          }}
                        />
                        <span style={{ color: field.labelColor }}>Otro</span>
                      </label>
                      {otherOptions[field.id] && (
                        <input
                          type="text"
                          className="w-full border ml-6"
                          name={`${field.id}-other`}
                          placeholder={
                            field.otherOptionPlaceholder ||
                            'Especifique otra opción...'
                          }
                          required
                          style={{
                            backgroundColor: field.backgroundColor,
                            color: field.labelColor,
                            borderColor: field.borderColor,
                            borderRadius:
                              field.borderRadius === 'rounded'
                                ? '0.375rem'
                                : field.borderRadius === 'rounded-md'
                                ? '0.5rem'
                                : field.borderRadius === 'rounded-lg'
                                ? '0.75rem'
                                : '0',
                            padding:
                              field.padding === 'p-2'
                                ? '0.5rem'
                                : field.padding === 'p-4'
                                ? '1rem'
                                : field.padding === 'p-6'
                                ? '1.5rem'
                                : '1rem',
                          }}
                        />
                      )}
                    </>
                  )}
                </div>
              ) : field.type === 'radio' ? (
                <div className="space-y-2">
                  {field.options?.map((option, index) => (
                    <label key={index} className="flex items-center gap-2">
                      <input
                        type="radio"
                        name={field.id}
                        value={option}
                        onChange={() =>
                          setOtherOptions({
                            ...otherOptions,
                            [field.id]: false,
                          })
                        }
                        style={{
                          accentColor: field.labelColor,
                          borderColor: field.borderColor,
                        }}
                      />
                      <span
                        style={{ color: field.labelColor }}
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(option, config),
                        }}
                      />
                    </label>
                  ))}
                  {field.includeOtherOption && (
                    <>
                      <label className="flex items-center gap-2">
                        <input
                          type="radio"
                          name={field.id}
                          value="otro"
                          onChange={() =>
                            setOtherOptions({
                              ...otherOptions,
                              [field.id]: true,
                            })
                          }
                          style={{
                            accentColor: field.labelColor,
                            borderColor: field.borderColor,
                          }}
                        />
                        <span style={{ color: field.labelColor }}>Otro</span>
                      </label>
                      {otherOptions[field.id] && (
                        <input
                          type="text"
                          className="w-full border ml-6"
                          name={`${field.id}-other`}
                          placeholder={
                            field.otherOptionPlaceholder ||
                            'Especifique otra opción...'
                          }
                          required
                          style={{
                            backgroundColor: field.backgroundColor,
                            color: field.labelColor,
                            borderColor: field.borderColor,
                            borderRadius:
                              field.borderRadius === 'rounded'
                                ? '0.375rem'
                                : field.borderRadius === 'rounded-md'
                                ? '0.5rem'
                                : field.borderRadius === 'rounded-lg'
                                ? '0.75rem'
                                : '0',
                            padding:
                              field.padding === 'p-2'
                                ? '0.5rem'
                                : field.padding === 'p-4'
                                ? '1rem'
                                : field.padding === 'p-6'
                                ? '1.5rem'
                                : '1rem',
                          }}
                        />
                      )}
                    </>
                  )}
                </div>
              ) : field.type === 'number' ? (
                <input
                  type="number"
                  className="w-full border"
                  name={field.id}
                  placeholder={field.placeholder || ''}
                  required={field.required}
                  style={{
                    backgroundColor: field.backgroundColor,
                    color: field.labelColor,
                    borderColor: field.borderColor,
                    borderRadius:
                      field.borderRadius === 'rounded'
                        ? '0.375rem'
                        : field.borderRadius === 'rounded-md'
                        ? '0.5rem'
                        : field.borderRadius === 'rounded-lg'
                        ? '0.75rem'
                        : '0',
                    padding:
                      field.padding === 'p-2'
                        ? '0.5rem'
                        : field.padding === 'p-4'
                        ? '1rem'
                        : field.padding === 'p-6'
                        ? '1.5rem'
                        : '1rem',
                  }}
                />
              ) : field.type === 'ticketSelector' ? (
                <select
                  className="w-full border"
                  name={field.id}
                  required={field.required || false}
                >
                  <option value="">Seleccionar ticket</option>
                  {Array.isArray(tickets) &&
                    tickets.map(ticket => (
                      <option
                        key={ticket.ticketNumber || ticket._id || ticket}
                        value={ticket.ticketNumber || ticket._id || ticket}
                      >
                        {ticket.ticketNumber || ticket._id || ticket}
                      </option>
                    ))}
                </select>
              ) : (
                <input
                  className="w-full border"
                  type={field.type}
                  name={field.id}
                  placeholder={field.placeholder || ''}
                  required={field.required}
                  min={
                    field.dateRestriction === 'past'
                      ? '1900-01-01'
                      : field.dateRestriction === 'future'
                      ? new Date().toISOString().split('T')[0] // Fecha de hoy
                      : null // Sin restricción
                  }
                  max={
                    field.dateRestriction === 'past'
                      ? new Date().toISOString().split('T')[0] // Fecha de hoy
                      : field.dateRestriction === 'future'
                      ? '2099-12-31'
                      : null // Sin restricción
                  }
                  style={{
                    backgroundColor: field.backgroundColor,
                    color: field.labelColor,
                    borderColor: field.borderColor,
                    borderRadius:
                      field.borderRadius === 'rounded'
                        ? '0.375rem'
                        : field.borderRadius === 'rounded-md'
                        ? '0.5rem'
                        : field.borderRadius === 'rounded-lg'
                        ? '0.75rem'
                        : '0',
                    padding:
                      field.padding === 'p-2'
                        ? '0.5rem'
                        : field.padding === 'p-4'
                        ? '1rem'
                        : field.padding === 'p-6'
                        ? '1.5rem'
                        : '1rem',
                  }}
                />
              )}
            </div>
          ))}
          {/* Añadir este campo oculto dentro del formulario */}
          <input type="hidden" id="fileUrlInput" name="fileUrl" />

          {/* Agregar este indicador de carga justo antes del botón de envío */}
          <div
            id="loadingIndicator"
            style={{ display: 'none' }}
            className="flex justify-center items-center mb-4"
          >
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
            <span className="ml-2">Procesando archivos...</span>
          </div>

          {/* Modificar el botón de envío para añadir el ID */}
          <button
            id="submitButton"
            type="submit"
            disabled={isSubmitDisabled}
            className={`transition-colors ${
              formData?.submitButton?.width || 'w-full'
            } 
  ${formData?.submitButton?.padding || 'p-4'} 
  ${formData?.submitButton?.size === 'small' ? 'text-sm p-2' : ''}
  ${formData?.submitButton?.size === 'large' ? 'text-lg p-6' : ''}
  flex ${
    formData?.submitButton?.position === 'left'
      ? 'justify-start'
      : formData?.submitButton?.position === 'right'
      ? 'justify-end'
      : 'justify-center'
  }`}
            style={{
              backgroundColor:
                formData?.submitButton?.backgroundColor || '#2563eb',
              color: formData?.submitButton?.textColor || '#ffffff',
              borderRadius: formData?.submitButton?.borderRadius || '0.375rem',
            }}
          >
            {isUploading ? (
              <span className="flex items-center">
                <svg
                  className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Procesando...
              </span>
            ) : (
              formData?.submitButton?.text || 'Enviar'
            )}
          </button>
        </form>
      ) : (
        <div className="text-red-500">No se encontró el formulario.</div>
      )}
      <footer className="border-t border-gray-200 py-4 mt-8">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center text-sm text-gray-600">
            <p>
              © {new Date().getFullYear()}{' '}
              <a
                href="https://yesbpo.com"
                className="hover:text-blue-600 transition-colors"
              >
                Yes Contact & Bpo S.A.S
              </a>
            </p>
            <p>Todos los derechos reservados</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ViewForm;